/* eslint-disable no-unused-vars */
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import { DropdownOption } from '@sl/components';
import { SETTLEMENT, SettlementClientId } from '@sl/features';
import { SharedModule } from '@sl/shared';
import { trackBy } from '@sl/utils';
import { GetSettlementsFilter } from 'src/app/core/features/settlement/infrastructure/repositories/settlement.repository.interface';

@Component({
  selector: 'sl-button-filter-liquidations-closed',
  templateUrl: './button-filter-liquidations-closed.component.html',
  styleUrls: ['./button-filter-liquidations-closed.component.scss'],
  standalone: true,
  imports: [SharedModule],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ButtonFilterLiquidationsClosedComponent implements OnInit {
  @Input() filters: FilterClosed = { ...defaultFilter };

  @Output() readonly filtersChange = new EventEmitter<FilterClosed>();
  @Output() readonly submitFilters = new EventEmitter<FilterClosed>();
  @Output() readonly discardFilters = new EventEmitter<FilterClosed>();

  initialFilters!: FilterClosed;
  visible = false;

  /* Section 1 */
  section1 = {
    legend: 'Filtrar por nº'
  };

  inputBatchId = {
    label: 'Buscar',
    placeholder: 'ID Lote',
    value: null as string | null
  };

  inputOrder = {
    label: 'Buscar por',
    placeholder: 'Nº Encargo',
    value: null as string | null
  };

  inputSinister = {
    label: 'Buscar por',
    placeholder: 'Nº Siniestro (NIS)',
    value: null as string | null
  };

  inputBill = {
    label: 'Buscar por',
    placeholder: 'Nº Factura',
    value: null as string | null
  };

  inputPolicy = {
    label: 'Buscar por',
    placeholder: 'Nº Póliza',
    value: null as string | null
  };

  /* Section 2 */
  section2 = {
    legend: 'Asegurado'
  };

  inputNameAndSurname = {
    label: 'Buscar por',
    placeholder: 'Nombre y Apellidos',
    value: null as string | null
  };

  /* Section 3 */
  section3 = {
    legend: 'Cliente'
  };

  dropdownClient = {
    label: 'Filtrar',
    placeholder: 'Cliente',
    options: [
      { label: 'Santalucía', value: SETTLEMENT.CLIENT_ID.SANTALUCIA },
      { label: 'Iris Global', value: SETTLEMENT.CLIENT_ID.IRIS }
    ] as DropdownOption<SettlementClientId>[],
    value: null as SettlementClientId | null
  };

  /* Section 4 */
  section4 = {
    legend: 'Importe'
  };

  inputAmount = {
    label: 'Buscar por',
    placeholder: 'Importe',
    value: null as any
  };

  constructor() {}

  ngOnInit(): void {
    this.initialFilters = { ...this.filters };
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['filters']) {
      this.inputBatchId.value = this.filters.batchId ?? null;
      this.inputPolicy.value = this.filters.policeId ?? null;
      this.inputOrder.value = this.filters.orderId ?? null;
      this.inputSinister.value = this.filters.claimId ?? null;
      this.inputBill.value = this.filters.billId ?? null;
      this.inputNameAndSurname.value = this.filters.insuredName ?? null;
      this.dropdownClient.value = (this.filters.clientId as SettlementClientId) ?? null;
      this.inputAmount.value = this.filters.amount ?? null;
    }
  }

  /* Action Buttons */

  handleSubmitFilters(): void {
    this.submitFilters.emit(this.filters);
    this.closePanel();
  }

  handleRemoveFilters(): void {
    this.resetFilters();
    this.discardFilters.emit(this.filters);
    this.closePanel();
  }

  /* Section 1 */

  onBatchId(): void {
    this.filters.batchId = this.inputBatchId.value || null;
    this.onChange();
  }

  onInputOrder(): void {
    this.filters.orderId = this.inputOrder.value || null;
    this.onChange();
  }

  onInputSinister(): void {
    this.filters.claimId = this.inputSinister.value || null;
    this.onChange();
  }

  onInputBill(): void {
    this.filters.billId = this.inputBill.value || null;
    this.onChange();
  }

  onInputPolicy(): void {
    this.filters.policeId = this.inputPolicy.value || null;
    this.onChange();
  }

  /* Section 2 */

  onInputNameAndSurname(): void {
    this.filters.insuredName = this.inputNameAndSurname.value || null;
    this.onChange();
  }

  /* Section 3 */

  onDropdownClient(): void {
    this.filters.clientId = this.dropdownClient.value || null;
    this.onChange();
  }

  /* Section 4 */

  onInputAmount(): void {
    this.filters.amount = this.inputAmount.value || null;
    this.onChange();
  }

  /* Utils */

  trackByFn(index: number, item: any): any {
    return trackBy(index, item);
  }

  /* Private */

  private onChange(): void {
    this.filtersChange.emit(this.filters);
  }

  private closePanel(): void {
    this.visible = false;
  }

  private resetFilters(): void {
    this.inputBatchId.value = null;
    this.inputOrder.value = null;
    this.inputSinister.value = null;
    this.inputBill.value = null;
    this.inputPolicy.value = null;
    this.inputNameAndSurname.value = null;
    this.dropdownClient.value = null;
    this.inputAmount.value = null;
    this.filters = { ...defaultFilter };
    this.onChange();
  }
}

export type FilterClosed = Pick<
  GetSettlementsFilter,
  'claimId' | 'orderId' | 'batchId' | 'policeId' | 'clientId' | 'billId' | 'insuredName' | 'amount'
>;

const defaultFilter: FilterClosed = {
  claimId: null,
  orderId: null,
  batchId: null,
  policeId: null,
  clientId: null,
  billId: null,
  insuredName: null,
  amount: null
} as const;

export const defaultFilterClosed: FilterClosed = { ...defaultFilter };
